import { useAuth } from "@app/hooks";
import { ProfileDialog } from "@app/components/organisms/Profile/ProfileDialog";
import {
  useState,
  MouseEvent,
  useEffect,
  SetStateAction,
  useCallback,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Typography,
  Menu,
  Avatar,
  MenuItem,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Badge,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import {
  Logout,
  ExpandMore,
  ExpandLess,
  DescriptionOutlined,
  GroupsOutlined,
} from "@mui/icons-material";
import {
  IconLogo,
  IconUser,
  IconSetting,
  IconInvalidUrl,
  IconLogoMobile,
} from "@app/components/icons";
import { DataDialog, DataTooltip } from "@app/components/atoms";
import {
  formatAnnouncementsBody,
  isMobile,
  resetIsContinuousLoading,
} from "@app/helpers";
import { RootState, store, setUser } from "@app/store";
import { UserInfo } from "@app/types";
import {
  getTOUVersionMaster,
  getUser,
  apiPutUser,
  apiGetUserAdminPermissions,
} from "@app/api";
import {
  AdminPermissions,
  INVALID_URL,
  LANGUAGES,
  RTL_LANGUAGES,
  SelectLanguage,
} from "@app/constants";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  apiDeleteAnnouncements,
  apiGetAnnouncements,
  apiGetUnreadCountAnnouncements,
  apiMarkReadAnnouncements,
} from "@app/api/announcements/announcements.api";
import { Announcements } from "@app/types/announcements.types";
import DoneIcon from "@mui/icons-material/Done";

import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";

export function Header() {
  const { t, i18n } = useTranslation();
  const { logout } = useAuth();
  const userInfo: UserInfo = useSelector(
    (state: RootState) => state.user.value,
  );
  const [userImageUrl, setUserImageUrl] = useState<string>("");
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const [userIsoLocaleCode, setUserIsoLocaleCode] = useState<string>("");
  const [givenName, setGivenName] = useState<string>("");
  const [surname, setSurname] = useState<string>("");
  const [userTimeFormat, setUserTimeFormat] = useState<string>("");

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [langTabValue, setLangTabValue] = useState(userIsoLocaleCode);
  const [timeFormatTabValue, setTimeFormatTabValue] = useState(userTimeFormat);

  const [touPdfUrl, setTouPdfUrl] = useState<string>("");
  const [privacyPolicyPdfUrl, setprivacyPolicyPdfUrl] = useState<string>("");
  const [openAnnouncements, setOpenAnnouncements] = useState(false);
  const theme = useTheme();
  const xsBreakPoint = useMediaQuery(theme.breakpoints.between("xs", "sm"));
  const [announcements, setAnnouncements] = useState<Announcements[]>();
  const [announcementsLoading, setAnnouncementsLoading] =
    useState<boolean>(false);
  const [announcementsCount, setAnnouncementsCount] = useState<number>();
  const [announcementBusy, setAnnouncementBusy] = useState<{
    id: string;
    action: "read" | "delete";
  } | null>(null);
  const notificationScrollRef = useRef<HTMLDivElement>(null);

  const [userAdminPermissions, setUserAdminPermissions] = useState<
    Partial<Record<AdminPermissions, boolean>>
  >({});

  const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
    fetchUserData();
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenLanguagesMenu = (event: MouseEvent<HTMLElement>) => {
    handleDialogOpen();
  };

  const handleCloseAnnouncementsMenu = () =>
    setOpenAnnouncements(!openAnnouncements);

  const getAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const db = await apiGetAnnouncements();
      setAnnouncements(db.data.data);
    } catch (error) {
      console.error("Failed to fetch announcements:", error);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  useEffect(() => {
    setUserImageUrl(userInfo.pictureUrl || "");
  }, [userInfo]);

  const fetchUserData = useCallback(async () => {
    try {
      const getUserAPI = await getUser();
      if (!getUserAPI?.data?.data) return;

      const {
        timeFormat,
        isoLocaleCode,
        givenName,
        surname,
        residenceCountryCode,
      } = getUserAPI.data.data;

      setUserTimeFormat(timeFormat);
      setUserIsoLocaleCode(isoLocaleCode);
      setGivenName(givenName);
      setSurname(surname);

      if (!residenceCountryCode) return;

      const touRes = await getTOUVersionMaster(residenceCountryCode);

      const tou = touRes?.data?.data?.find((i: any) => i.type === "TOU");
      const privacy = touRes?.data?.data?.find(
        (i: any) => i.type === "PRIVACY_POLICY",
      );

      if (tou?.pdfUrl) setTouPdfUrl(tou.pdfUrl);
      if (privacy?.pdfUrl) setprivacyPolicyPdfUrl(privacy.pdfUrl);

      getUnreadCountAnnouncements();
      fetchUserPermissions();
      if (!getUserAPI?.data?.data) resetIsContinuousLoading();
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      resetIsContinuousLoading();
    }
  }, []);

  const fetchUserPermissions = async () => {
    try {
      const permissionResponse = await apiGetUserAdminPermissions();
      if (permissionResponse?.data?.data) {
        setUserAdminPermissions(permissionResponse.data.data);
      }
    } catch (error) {
      console.error("Error fetching user permissions:", error);
    }
  };

  const getUnreadCountAnnouncements = async () => {
    const getAnnouncementsCountAPI = await apiGetUnreadCountAnnouncements();
    setAnnouncementsCount(getAnnouncementsCountAPI.data?.data?.unreadCount);
  };

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    setLangTabValue(userIsoLocaleCode);
    setTimeFormatTabValue(userTimeFormat);
  }, [userIsoLocaleCode, userTimeFormat]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleProfileSaved = (pictureUrl: string) => {
    fetchUserData();
    store.dispatch(
      setUser({
        fullName: `${surname} ${givenName}`,
        pictureUrl,
      }),
    );
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setLangTabValue(userIsoLocaleCode);
    setTimeFormatTabValue(userTimeFormat);
  };

  const handleLangTabChange = (event: {
    target: { value: SetStateAction<string> };
  }) => {
    setLangTabValue(event.target.value);
  };

  const handleTimeFormatTabChange = (event: {
    target: { value: SetStateAction<string> };
  }) => {
    setTimeFormatTabValue(event.target.value);
  };

  const handleSave = async () => {
    const payload = {
      timeFormat: timeFormatTabValue,
      isoLocaleCode: langTabValue,
    };

    const isUnchanged =
      langTabValue === userIsoLocaleCode &&
      timeFormatTabValue === userTimeFormat;
    if (isUnchanged) {
      setDialogOpen(false);
      return;
    }

    try {
      const response = await apiPutUser(payload);
      if (response?.status === 200) {
        window.location.reload();
      } else {
        console.error("Failed to update user:", response);
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }

    setDialogOpen(false);
  };

  const isSaveDisabled = langTabValue === "" || timeFormatTabValue === "";

  async function handleDeleteAnnouncement(
    announcementId: string,
  ): Promise<void> {
    if (announcementBusy) return;
    const scrollEl = notificationScrollRef.current;
    const prevScrollTop = scrollEl?.scrollTop ?? 0;
    setAnnouncementBusy({ id: announcementId, action: "delete" });
    try {
      await apiDeleteAnnouncements([announcementId]);
      setAnnouncements((prev) =>
        prev?.filter((a) => a.contentData.announcementId !== announcementId),
      );
      await getUnreadCountAnnouncements();
      requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = prevScrollTop;
      });
    } catch (error) {
      console.error("Failed to delete announcement:", error);
      await getAnnouncements();
      await getUnreadCountAnnouncements();
    } finally {
      setAnnouncementBusy(null);
    }
  }

  async function handleMarkReadAnnouncement(
    announcementId: string,
    isRead: boolean | null,
  ): Promise<void> {
    if (isRead || announcementBusy) return;
    setAnnouncementBusy({ id: announcementId, action: "read" });
    try {
      await apiMarkReadAnnouncements([announcementId]);
      setAnnouncements((prev) =>
        prev?.map((a) =>
          a.contentData.announcementId === announcementId
            ? {
                ...a,
                contentData: { ...a.contentData, isRead: true },
              }
            : a,
        ),
      );
      await getUnreadCountAnnouncements();
    } catch (error) {
      console.error("Failed to mark announcement as read:", error);
      await getAnnouncements();
      await getUnreadCountAnnouncements();
    } finally {
      setAnnouncementBusy(null);
    }
  }

  const currentLang = i18n.language.split("-")[0] as SelectLanguage;
  const isRTL = RTL_LANGUAGES.includes(currentLang);
  return (
    <header className="page-header">
      <div className="container">
        <div className="row flex-centered">
          <Link to="/">{isMobile() ? <IconLogoMobile /> : <IconLogo />}</Link>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              maxWidth: "50%",
              flexGrow: 0,
              marginInlineStart: "auto",
              flexDirection: "row",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={handleOpenUserMenu}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="inherit"
              sx={{
                order: isRTL ? 1 : 3,
                p: 0,
                marginInlineStart: isRTL ? "-6px" : 0,
                marginInlineEnd: isRTL ? 0 : "-6px",
              }}
            >
              {!!anchorElUser ? <ExpandLess /> : <ExpandMore />}
            </IconButton>

            <Typography
              variant="h4"
              noWrap={xsBreakPoint}
              component="div"
              sx={{
                order: 2,
                fontSize: 14,
                whiteSpace: "nowrap",
                mx: 0.5,
              }}
            >
              <DataTooltip isTextPre width="100%">
                {userIsoLocaleCode === "ja"
                  ? `${surname} ${givenName}`
                  : `${givenName} ${surname}`}
              </DataTooltip>
            </Typography>

            <Box sx={{ order: isRTL ? 3 : 1 }}>
              {userImageUrl ? (
                userImageUrl === INVALID_URL ? (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginInlineEnd: 0.75,
                    }}
                  >
                    <Badge
                      badgeContent={announcementsCount}
                      sx={{
                        width: 32,
                        height: 32,
                        "& .MuiBadge-badge": {
                          backgroundColor: "#140a9a",
                          color: "#fff !important",
                        },
                      }}
                    >
                      <IconInvalidUrl />
                    </Badge>
                  </Box>
                ) : (
                  <Badge
                    badgeContent={announcementsCount}
                    sx={{
                      width: 32,
                      height: 32,
                      "& .MuiBadge-badge": {
                        backgroundColor: "#140a9a",
                        color: "#fff !important",
                      },
                      marginInlineEnd: 0.75,
                    }}
                  >
                    <Avatar
                      sx={{ width: 32, height: 32 }}
                      alt="avatar"
                      src={userInfo.pictureUrl}
                      imgProps={{ onError: () => setUserImageUrl(INVALID_URL) }}
                    />
                  </Badge>
                )
              ) : (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginInlineEnd: 0.75,
                  }}
                >
                  <Badge
                    badgeContent={announcementsCount}
                    sx={{
                      width: 32,
                      height: 32,
                      "& .MuiBadge-badge": {
                        backgroundColor: "#140a9a",
                        color: "#fff !important",
                      },
                    }}
                  >
                    <IconUser />
                  </Badge>
                </Box>
              )}
            </Box>
          </Box>

          <Menu
            sx={{ mt: "31.5px", ml: "15px" }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={!!anchorElUser}
            onClose={handleCloseUserMenu}
          >
            <MenuItem
              sx={{ p: "8px 12px" }}
              onClick={() => {
                setProfileDialogOpen(true);
                handleCloseUserMenu();
              }}
            >
              <AccountCircleOutlinedIcon />
              <Typography sx={{ marginInlineStart: "4px" }} textAlign="center">
                {t("profile.dialogTitle")}
              </Typography>
            </MenuItem>

            <span
              onClick={(event) => {
                handleOpenLanguagesMenu(event);
                handleCloseUserMenu();
                fetchUserData();
              }}
            >
              <MenuItem
                aria-label="languages of current user"
                aria-controls="menu-languages"
                aria-haspopup="true"
                sx={{ p: "8px 12px" }}
              >
                <IconSetting />
                <Typography
                  sx={{ marginInlineStart: "8px" }}
                  textAlign="center"
                >
                  {t("UserSettings")}
                </Typography>
              </MenuItem>
            </span>

            {userAdminPermissions.USER_SEARCH && (
              <Link to="/search-user" onClick={handleCloseUserMenu}>
                <MenuItem sx={{ p: "8px 12px" }}>
                  <SearchOutlinedIcon />
                  <Typography marginInlineStart={"8px"} textAlign="center">
                    {t("permission.USER_SEARCH")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            {userAdminPermissions.USER_MANAGE && (
              <Link to="/search-user?mode=manage" onClick={handleCloseUserMenu}>
                <MenuItem sx={{ p: "8px 12px" }}>
                  <GroupsOutlined />
                  <Typography marginInlineStart={"8px"} textAlign="center">
                    {t("permission.USER_MANAGE")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            {userAdminPermissions.MANAGE_TOU && (
              <Link
                to="/manage-tou"
                state={{ mode: "manage" }}
                onClick={handleCloseUserMenu}
              >
                <MenuItem sx={{ p: "8px 12px" }}>
                  <DescriptionOutlined />
                  <Typography marginInlineStart={"8px"} textAlign="center">
                    {t("permission.MANAGE_TOU")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            <span
              onClick={() => {
                handleCloseAnnouncementsMenu();
                handleCloseUserMenu();
                getAnnouncements();
              }}
            >
              <MenuItem
                aria-label="Announcements of current user"
                aria-controls="menu-announcements"
                aria-haspopup="true"
                sx={{ p: "8px 12px" }}
              >
                <Badge
                  badgeContent={announcementsCount}
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#140a9a",
                      color: "#fff !important",
                    },
                  }}
                >
                  <CampaignOutlinedIcon />
                </Badge>
                <Typography
                  sx={{ marginInlineStart: "8px" }}
                  textAlign="center"
                >
                  {t("announcements")}
                </Typography>
              </MenuItem>
            </span>

            {userAdminPermissions.ANNOUNCEMENTS && (
              <Link to="/create-announcement" onClick={handleCloseUserMenu}>
                <MenuItem sx={{ p: "8px 12px" }}>
                  <CampaignOutlinedIcon />
                  <Typography marginInlineStart={"8px"} textAlign="center">
                    {t("permission.ANNOUNCEMENTS")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            {(userAdminPermissions.TRANSLATIONS_ADMIN ||
              userAdminPermissions.TRANSLATIONS_USERS) && (
              <Link to="/manage-translations" onClick={handleCloseUserMenu}>
                <MenuItem sx={{ p: "8px 12px" }}>
                  <TranslateOutlinedIcon />
                  <Typography
                    sx={{ marginInlineStart: "8px" }}
                    textAlign="center"
                  >
                    {t("manageTranslations")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            {userAdminPermissions.PERIODIC_MAINTENANCE && (
              <Link
                to="/manage-periodic-maintenance"
                onClick={handleCloseUserMenu}
              >
                <MenuItem sx={{ p: "8px 12px" }}>
                  <BuildOutlinedIcon />
                  <Typography
                    sx={{ marginInlineStart: "8px" }}
                    textAlign="center"
                  >
                    {t("managePeriodicMaintenance")}
                  </Typography>
                </MenuItem>
              </Link>
            )}

            {touPdfUrl && (
              <a
                href={touPdfUrl}
                rel="noreferrer"
                target="_blank"
                onClick={handleCloseUserMenu}
              >
                <MenuItem sx={{ p: "8px 12px" }}>
                  <DescriptionOutlined className="txt-gray-3" />
                  <Typography
                    sx={{ marginInlineStart: "8px" }}
                    textAlign="center"
                  >
                    {t("dialogDataUsage.label1")}
                  </Typography>
                </MenuItem>
              </a>
            )}

            {privacyPolicyPdfUrl && (
              <a
                href={privacyPolicyPdfUrl}
                rel="noreferrer"
                target="_blank"
                onClick={handleCloseUserMenu}
              >
                <MenuItem sx={{ p: "8px 12px" }}>
                  <DescriptionOutlined className="txt-gray-3" />
                  <Typography
                    sx={{ marginInlineStart: "8px" }}
                    textAlign="center"
                  >
                    {t("privacyPolicy")}
                  </Typography>
                </MenuItem>
              </a>
            )}

            <Link to="/license" onClick={handleCloseUserMenu}>
              <MenuItem sx={{ p: "8px 12px" }}>
                <DescriptionOutlined className="txt-gray-3" />
                <Typography
                  sx={{ marginInlineStart: "8px" }}
                  textAlign="center"
                >
                  {t("license.title")}
                </Typography>
              </MenuItem>
            </Link>

            <Link to="/trademark" onClick={handleCloseUserMenu}>
              <MenuItem sx={{ p: "8px 12px" }}>
                <DescriptionOutlined className="txt-gray-3" />
                <Typography
                  sx={{ marginInlineStart: "8px" }}
                  textAlign="center"
                >
                  {t("tradeMark.title")}
                </Typography>
              </MenuItem>
            </Link>

            <MenuItem
              sx={{ p: "8px 12px" }}
              onClick={() => {
                logout(true);
              }}
            >
              <Logout className="txt-gray-3" />
              <Typography sx={{ marginInlineStart: "8px" }} textAlign="center">
                {t("signOut")}
              </Typography>
            </MenuItem>
          </Menu>
        </div>
      </div>

      <ProfileDialog
        open={profileDialogOpen}
        onClose={() => setProfileDialogOpen(false)}
        onSaved={handleProfileSaved}
      />

      <DataDialog
        disableBackdropClick
        open={dialogOpen}
        title={<>{t("UserSettings")}</>}
        onClose={(event: any, reason: string) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleDialogClose();
          }
        }}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "700px",
            maxWidth: "700px",
            height: "auto",
            maxHeight: "auto",
          },
        }}
      >
        <DialogContent>
          <Box sx={{ mt: 0 }}>
            <TextField
              select
              label={t("Language")}
              value={langTabValue}
              onChange={handleLangTabChange}
              variant="outlined"
              fullWidth
              sx={{
                mt: 1,
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
              style={{
                flex: 1,
                minWidth: "250px",
              }}
            >
              {LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              select
              label={t("DateFormat")}
              value={timeFormatTabValue}
              onChange={handleTimeFormatTabChange}
              variant="outlined"
              fullWidth
              sx={{
                mt: 1,
                borderRadius: "8px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
              style={{
                flex: 1,
                minWidth: "250px",
              }}
            >
              <MenuItem value="yyyy/MM/dd">yyyy/MM/dd</MenuItem>
              <MenuItem value="MM/dd/yyyy">MM/dd/yyyy</MenuItem>
              <MenuItem value="dd/MM/yyyy">dd/MM/yyyy</MenuItem>
            </TextField>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Link
              to="/delete-account"
              onClick={() => {
                handleCloseUserMenu();
                setDialogOpen(false);
              }}
              style={{ textDecoration: "none" }}
            >
              <Button className="btn btn-primary bg-red">
                {t("deleteAccount.name")}
              </Button>
            </Link>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
          <Button
            onClick={handleDialogClose}
            style={{ width: "auto" }}
            className="btn btn-no-border"
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            className="btn btn-primary"
            style={{ width: "auto" }}
            disabled={isSaveDisabled}
          >
            {t("formAction.save")}
          </Button>
        </DialogActions>
      </DataDialog>

      <DataDialog
        disableBackdropClick
        title={
          <>
            <CampaignOutlinedIcon sx={{ fontSize: 30 }} /> {t("announcements")}
          </>
        }
        open={openAnnouncements}
        setOpen={setOpenAnnouncements}
        dialogActions={
          <div className="flex align-items-center justify-content-end">
            <Button
              onClick={handleCloseAnnouncementsMenu}
              className="btn btn-no-border"
              style={{ width: "auto" }}
            >
              {t("dialogRefuel.close")}
            </Button>
          </div>
        }
      >
        <div className="notification-wrapper mt-5" ref={notificationScrollRef}>
          {announcementsLoading ? (
            <div className="notification-list">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="notification-card">
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <div className="notification-header" style={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={20} />
                      </div>
                      <div
                        className="action-btn-div"
                        style={{ display: "flex", gap: 8 }}
                      >
                        <Skeleton variant="circular" width={24} height={24} />
                        <Skeleton variant="circular" width={24} height={24} />
                      </div>
                    </div>
                    <Skeleton
                      variant="text"
                      width="100%"
                      height={16}
                      sx={{ mt: 0.5 }}
                    />
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={16}
                      sx={{ mt: 0.5 }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : announcements == null ? null : announcements.length > 0 ? (
            <div className="notification-list">
              {announcements.map((announcement) => {
                const announcementId = announcement.contentData.announcementId;
                const isRowBusy = announcementBusy?.id === announcementId;
                const actionsLocked = !!announcementBusy;

                return (
                  <div key={announcementId} className="notification-card">
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <div className="notification-header">
                          {!announcement.contentData.isRead && (
                            <span className="unread-dot" />
                          )}
                          <p className="notification-title">
                            {announcement?.title}
                          </p>
                        </div>
                        <div className="action-btn-div">
                          <button
                            className="check-btn"
                            disabled={
                              announcement.contentData.isRead || actionsLocked
                            }
                            onClick={(e) => {
                              if (announcement.contentData.isRead) return;

                              handleMarkReadAnnouncement(
                                announcementId,
                                announcement.contentData.isRead,
                              );
                            }}
                            aria-label={
                              announcement.contentData.isRead
                                ? "Announcement already read"
                                : "Mark announcement as read"
                            }
                          >
                            {isRowBusy &&
                            announcementBusy?.action === "read" ? (
                              <CircularProgress
                                size={20}
                                sx={{ color: "#140a9a" }}
                              />
                            ) : (
                              <DoneIcon />
                            )}
                          </button>
                          <button
                            className="delete-btn"
                            disabled={actionsLocked}
                            onClick={(e) => {
                              handleDeleteAnnouncement(announcementId);
                            }}
                            aria-label="Delete Announcement"
                          >
                            {isRowBusy &&
                            announcementBusy?.action === "delete" ? (
                              <CircularProgress
                                size={20}
                                sx={{ color: "#140a9a" }}
                              />
                            ) : (
                              <DeleteOutlinedIcon />
                            )}
                          </button>
                        </div>
                      </div>
                      <p
                        className="notification-body"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {(() => {
                          const body = announcement?.body || "";
                          const localeDateStart = t(
                            "announcementDateStartMatch",
                          );
                          const localeDateEnd = t("announcementDateEndMatch");
                          const localeDateNotice = t(
                            "announcementDateNoticeMatch",
                          );
                          const { formattedBody: updatedBody, url } =
                            formatAnnouncementsBody(
                              body,
                              localeDateStart,
                              localeDateEnd,
                              localeDateNotice,
                              userIsoLocaleCode,
                              userTimeFormat,
                            );

                          if (!url) return updatedBody;
                          const parts = updatedBody.split(url);

                          return (
                            <>
                              {parts[0]}
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="notification-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {url}
                              </a>
                              {parts[1]}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="fs-16 txt-gray-1 m-0">{t("noAnnouncementsFound")}</p>
          )}
        </div>
      </DataDialog>
    </header>
  );
}
